import { useState, useEffect } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';

import Button from '../../components/Button/Button';
import PlusIcon from '../../assets/Icons/components/PlusIcon';
import Table from '../../components/Table/Table';
import Chip from '../../components/Chip/Chip';
import Icons from '../../assets/index';
import FactSheet from '../../components/Dashboard/FactSheet/FactSheet';
import Popup from '../../components/Popup/Popup';

import { useGetAllFactSheetsQuery } from '../../services/plaintiffSheets/plaintiffSheetsEndpoint';
import { useDispatch } from 'react-redux';
import { setSidebarCollapsed } from '@/reducers/app-global-slice';
import { Tooltip } from '@mui/material';

export type Lawsuit = {
  id: string;
  name: string;
  status?: string;
};

const PlaintiffSheets = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isPopUpOpen, setIsPopUpOpen] = useState(false);

  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageCount, setPageCount] = useState<number>(0);

  const {
    data: FactSheetData,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetAllFactSheetsQuery({
    pageSize: pageSize,
    pageNumber: pageIndex + 1,
  });

  useEffect(() => {
    if (FactSheetData?.pagination?.total_item_count) {
      const totalPages = Math.ceil(FactSheetData.pagination.total_item_count / pageSize);
      setPageCount(totalPages);
    }
  }, [FactSheetData, pageSize]);

  const columnHelper = createColumnHelper<Lawsuit>();

  const columns: ColumnDef<Lawsuit, string>[] = [
    columnHelper.accessor('name', {
      header: () => <div>Sheet Name</div>,
      cell: (info) => (
        <div className="break-words whitespace-pre-wrap break-all">{info.getValue()}</div>
      ),
    }),

    columnHelper.accessor('status', {
      header: () => <div>Status</div>,
      cell: (info) => (
        <div>
          <Chip
            title={info.getValue() === 'PUBLISH' ? 'PUBLISHED' : 'DRAFT'}
            isFormatting={false}
            size="sm"
          />
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-center">Action</div>,
      cell: ({ row }) => {
        const rowData = row.original;
        const status = rowData.status === 'PUBLISH' ? true : false;

        return (
          <div className="flex space-x-4 justify-center">
            {status ? (
              <button className="text-gray-300 opacity-50 cursor-not-allowed" disabled>
                <Icons.editIcon />
              </button>
            ) : (
              <Tooltip
                data-testid="toggle-arrow-container"
                placement="right"
                arrow
                title="Edit sheet"
              >
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => navigate(`/plaintiffSheets/section/${rowData.id}`)}
                >
                  <Icons.editIcon />
                </button>
              </Tooltip>
            )}

            <Tooltip
              data-testid="toggle-arrow-container"
              placement="right"
              arrow
              title={!status ? 'View sheet' : ''}
            >
              <button
                className=" hover:text-gray-700"
                onClick={() => {
                  dispatch(setSidebarCollapsed(true));
                  navigate('/plaintiffSheets/preview');
                }}
              >
                <Icons.viewIcon />
              </button>
            </Tooltip>
          </div>
        );
      },
    }),
  ];

  // Handlers
  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(0); // Reset to first page on size change
  };

  useEffect(() => {
    if (FactSheetData) refetch();
  }, []);

  return (
    <div className="flex flex-col bg-white rounded-lg p-4 h-full ">
      <div className="flex justify-between items-center">
        <p className="text-xl font-medium text-[#4F4F4F]">Plaintiff Sheets</p>
        <Button
          size="lg"
          color="outlined"
          startIcon={<PlusIcon />}
          onClick={() => setIsPopUpOpen(true)}
        >
          New Fact Sheet
        </Button>
      </div>

      <div className="w-full flex-grow pt-6">
        {isError ? (
          <div className="text-red-500">Error fetching data: {JSON.stringify(error)}</div>
        ) : (
          <Table
            data={(FactSheetData?.data as Lawsuit[]) || []}
            columns={columns}
            isLoading={isLoading || isFetching}
            pagination={{
              pageIndex,
              pageSize,
              pageCount,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            totalItems={FactSheetData?.pagination?.total_item_count || 0}
            maxHeight="calc(100vh - 180px)"
            pageTitle="Sheets per page"
          />
        )}
      </div>

      {isPopUpOpen && (
        <Popup isOpen={isPopUpOpen} title="New Fact Sheet" onClose={() => setIsPopUpOpen(false)}>
          <FactSheet closePopUp={() => setIsPopUpOpen(false)} />
        </Popup>
      )}
    </div>
  );
};

export default PlaintiffSheets;
